import PointerInteraction from 'ol/interaction/Pointer';
import Collection from 'ol/Collection';
import { TRUE } from 'ol/functions';
import VectorSource from 'ol/source/Vector';
import { getCenter } from 'ol/extent';
import Style from 'ol/style/Style';
import Point from 'ol/geom/Point';
import Circle from 'ol/style/Circle';
import RegularShape from 'ol/style/RegularShape';

import { Stroke } from 'cadenza/map/style/stroke';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { WHITE_FILL } from 'cadenza/map/style/select-styles';
import { Oval } from 'cadenza/map/geom/oval';
import { OverlayVectorLayer } from 'cadenza/map/interactions/overlay-vector-layer';
import { TranslateEvent, TranslateEventType } from 'cadenza/map/interactions/translate-event';

/**
 * Translate by handle interaction is inspired by ol.interaction.Translate, but:
 *  - for each feature a drag handle is added in the geometry center,
 *  - features can be translated only by dragging the drag handle (and not any geometry interior point like in OL Translate interaction),
 *  - only one feature is translated (not all currently selected features like in OL Translate interaction).
 *
 *  @class
 */
export class TranslateByHandleInteraction extends PointerInteraction {

  _lastCoordinate;
  _startCoordinate;
  _features;
  _filter;
  _hitTolerance;
  _lastFeature;

  // An overlay vector layer to display drag handles
  _overlayVectorLayer;

  /**
   * Creates the TranslateByHandleInteraction
   *
   * @param {object} options
   * @param {Collection<Feature>} options.features
   * @param {FilterFunction} options.filter
   * @param {number} options.hitTolerance
   */
  constructor ({ features = null, filter = TRUE, hitTolerance = 0, ...options } = {}) {
    super(options);
    this._lastCoordinate = null;
    this._startCoordinate = null;
    this._features = features;
    this._filter = filter;
    this._hitTolerance = hitTolerance;
    this._lastFeature = null;
    this._overlayVectorLayer = new OverlayVectorLayer({
      source: new VectorSource({ features }),
      style: createDragHandleStyle,
      getMap: () => this.getMap()
    });
    this._overlayVectorLayerForMouseover = new OverlayVectorLayer({
      source: new VectorSource({ features: new Collection() }),
      style: createMouseoverDragHandleStyle,
      getMap: () => this.getMap()
    });
  }

  setMap (map) {
    super.setMap(map);
    this._overlayVectorLayer.setMap(map);
    this._overlayVectorLayerForMouseover.setMap(map);
  }

  handleMoveEvent (event) {
    const hoverFeaturesCollection = this._overlayVectorLayerForMouseover.getSource().getFeaturesCollection();
    hoverFeaturesCollection.clear();

    const feature = this._featuresAtPixel(event.pixel, event.map);
    if (feature) {
      hoverFeaturesCollection.push(feature);
    }
  }

  handleDownEvent (event) {
    this._lastFeature = this._featuresAtPixel(event.pixel, event.map);
    if (!this._lastCoordinate && this._lastFeature) {
      this._startCoordinate = event.coordinate;
      this._lastCoordinate = event.coordinate;
      this.handleMoveEvent(event);

      this.dispatchEvent(
        new TranslateEvent(
          TranslateEventType.TRANSLATE_START,
          new Collection([ this._lastFeature ]),
          event.coordinate,
          this._startCoordinate,
          event
        )
      );
      return true;
    }
    return false;
  }

  handleUpEvent (event) {
    if (this._lastCoordinate) {
      this._lastCoordinate = null;
      this.handleMoveEvent(event);

      this.dispatchEvent(
        new TranslateEvent(
          TranslateEventType.TRANSLATE_END,
          new Collection([ this._lastFeature ]),
          event.coordinate,
          this._startCoordinate,
          event
        )
      );
      // cleanup
      this._startCoordinate = null;
      return true;
    }
    return false;
  }

  handleDragEvent (event) {
    if (this._lastCoordinate) {
      const newCoordinate = event.coordinate;
      const deltaX = newCoordinate[0] - this._lastCoordinate[0];
      const deltaY = newCoordinate[1] - this._lastCoordinate[1];

      const feature = this._lastFeature;

      const geom = feature.getGeometry();
      geom.translate(deltaX, deltaY);
      feature.setGeometry(geom);

      this._lastCoordinate = newCoordinate;
      this.dispatchEvent(
        new TranslateEvent(
          TranslateEventType.TRANSLATING,
          new Collection([ feature ]),
          newCoordinate,
          this._startCoordinate,
          event
        )
      );
    }
  }

  _featuresAtPixel (pixel, map) {
    return map.forEachFeatureAtPixel(
      pixel,
      feature => {
        if (this._features.getArray().includes(feature)) {
          return feature;
        }
      },
      {
        layerFilter: layer => layer === this._overlayVectorLayer,
        hitTolerance: this._hitTolerance
      }
    );
  }

}

const DRAG_HANDLE_STROKE = new Stroke({ color: '#7ABDF5', width: 1 });
const DRAG_HANDLE_ARROWS = [
  { angle: 0, displacement: [ 0, 7 ] }, // top
  { angle: Math.PI, displacement: [ 0, -7 ] }, // bottom
  { angle: Math.PI / 2, displacement: [ 7, 0 ] }, // right
  { angle: -Math.PI / 2, displacement: [ -7, 0 ] } // left
];

function createDragHandleStyle (feature, resolution) {
  if (!feature || !feature.getGeometry()) {
    return null;
  }
  const centerPoint = getGeometryCenter(feature);
  return new Style({
    image: new Circle({
      fill: WHITE_FILL,
      radius: 4,
      stroke: DRAG_HANDLE_STROKE
    }),
    zIndex: Infinity,
    geometry: centerPoint
  });
}

function createMouseoverDragHandleStyle (feature, resolution) {
  if (!feature || !feature.getGeometry()) {
    return null;
  }
  const centerPoint = getGeometryCenter(feature);
  return DRAG_HANDLE_ARROWS.map(({ angle, displacement }) =>
    new Style({
      image: new RegularShape({
        points: 3,
        radius: 3,
        angle,
        fill: WHITE_FILL,
        displacement
      }),
      zIndex: Infinity,
      geometry: centerPoint
    })
  );
}

function getGeometryCenter (feature) {
  const geometry = feature.getGeometry();
  if (geometry.getType() === GeometryType.POLYGON) {
    return geometry.getInteriorPoint();
  } else if (geometry.getType() === GeometryType.MULTI_POLYGON) {
    return geometry.getInteriorPoints().getPoint(0);
  } else if (geometry.getType() === GeometryType.LINE_STRING && !(geometry instanceof Oval)) {
    return new Point(geometry.getFlatMidpoint());
  } else if (geometry.getType() === GeometryType.MULTI_LINE_STRING) {
    const [ firstMidpointX, firstMidpointY ] = geometry.getFlatMidpoints();
    return new Point([ firstMidpointX, firstMidpointY ]);
  }
  // By default return the center of the geometry extent, works well for an ovals, rectangles, circles and points.
  const centerCoordinate = getCenter(geometry.getExtent());
  return new Point(centerCoordinate);
}
